import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, CircleNotch, Trash, X } from "@phosphor-icons/react";
import { extractApiError } from "../lib/errors";
import { api } from "../api";

export function AvatarUpload({
  currentUrl,
  size = 96,
  editable = true,
  withRing = true,
  fallbackInitial,
}: {
  currentUrl: string | null;
  size?: number;
  editable?: boolean;
  withRing?: boolean;
  fallbackInitial?: string;
}) {
  const { t } = useTranslation("common");
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Optimistic preview: показываем blob URL сразу после crop, до re-fetch
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imgLoadErr, setImgLoadErr] = useState(false);

  // Когда сервер вернул реальный URL — очищаем blob preview
  useEffect(() => {
    if (previewBlobUrl && currentUrl) {
      const t = setTimeout(() => {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [currentUrl, previewBlobUrl]);

  // Сбрасываем ошибку загрузки при смене URL
  useEffect(() => { setImgLoadErr(false); }, [currentUrl, previewBlobUrl]);

  const upload = useMutation({
    mutationFn: async (file: Blob) => {
      const fd = new FormData();
      fd.append("file", file, "avatar.jpg");
      const r = await api.post("/athlete/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setUploading(false);
    },
    onError: (e: unknown) => {
      setUploading(false);
      if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
      setError(extractApiError(e, "ошибка загрузки"));
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const r = await api.delete("/athlete/profile/avatar");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const onPick = () => inputRef.current?.click();

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError(t("avatar.file_too_large", "Файл больше 5 MB")); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setError(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const onSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedImage(imageSrc, croppedAreaPixels);
      // Optimistic: показываем сразу, закрываем кроппер
      const objectUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(objectUrl);
      setImageSrc(null);
      setUploading(true);
      upload.mutate(blob);
    } catch (err) {
      setError(extractApiError(err, "ошибка обрезки"));
    }
  };

  const baseURL = (() => {
    try {
      const env = import.meta.env?.VITE_API_BASE;
      return typeof env === "string" ? env : "";
    } catch { return ""; }
  })();
  // previewBlobUrl (blob:) → fullUrl (relative → absolute) → null
  const displayUrl = previewBlobUrl ?? (currentUrl ? baseURL + currentUrl : null);

  const ringClasses = withRing
    ? "p-[3px] bg-gradient-to-tr from-accent via-indigo-300 to-indigo-500 animate-[spin_8s_linear_infinite]"
    : "";

  return (
    <>
      <div className="relative inline-flex items-center justify-center">
        <div className={`rounded-full ${ringClasses}`} style={{ width: size + 6, height: size + 6 }}>
          <div
            className="rounded-full bg-bg-base ring-1 ring-bg-divider overflow-hidden flex items-center justify-center"
            style={{ width: size, height: size }}
          >
            {displayUrl && !imgLoadErr ? (
              <img
                src={displayUrl}
                alt="avatar"
                className="w-full h-full object-cover"
                onError={() => setImgLoadErr(true)}
              />
            ) : (
              <span className="text-2xl font-bold text-fg-muted">
                {(fallbackInitial || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* Loading overlay */}
        {uploading && (
          <div
            className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"
            style={{ width: size + 6, height: size + 6 }}
          >
            <CircleNotch className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        {editable && (
          <button
            type="button"
            onClick={onPick}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-accent text-accent-on flex items-center justify-center ring-2 ring-bg-base shadow-[var(--shadow-tooltip)] hover:brightness-110 disabled:opacity-50"
            aria-label={t("a11y.upload_photo")}
          >
            {uploading
              ? <CircleNotch className="w-3.5 h-3.5 animate-spin" />
              : <Camera className="w-3.5 h-3.5" />
            }
          </button>
        )}
      </div>

      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />

      {error && !imageSrc && (
        <div className="text-rose-400 text-[11px] mt-1 text-center">{error}</div>
      )}

      {imageSrc && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm font-semibold text-white">
              {t("avatar_upload.adjust_frame", { defaultValue: "Подгони рамку" })}
            </div>
            <button
              type="button"
              onClick={() => setImageSrc(null)}
              className="text-white/60 hover:text-white"
              aria-label={t("a11y.clear_image", { defaultValue: "Очистить выбранное изображение" })}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 relative">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="px-4 py-3 border-t border-white/10 space-y-3">
            <input
              type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              aria-label={t("a11y.zoom_image", { defaultValue: "Масштаб изображения" })}
              className="w-full accent-accent"
            />
            {error && <div className="text-rose-400 text-[12px]">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setImageSrc(null)} className="flex-1 px-3 py-2 rounded-pill ring-1 ring-white/20 text-white text-[13px]">
                {t("avatar.cancel", "Отмена")}
              </button>
              <button type="button" onClick={onSave} className="flex-1 px-3 py-2 rounded-pill bg-accent text-accent-on font-medium text-[13px]">
                {t("avatar.save", "Сохранить")}
              </button>
            </div>
          </div>
        </div>
      )}

      {editable && currentUrl && !imageSrc && (
        <button
          type="button"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          className="text-[10px] text-fg-muted hover:text-rose-300 mt-2 inline-flex items-center gap-1"
        >
          <Trash className="w-3 h-3" />
          {t("avatar.remove", "Удалить фото")}
        </button>
      )}
    </>
  );
}


async function getCroppedImage(imageSrc: string, area: Area): Promise<Blob> {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas context");
  const targetSize = Math.min(1024, area.width);
  canvas.width = targetSize;
  canvas.height = targetSize;
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, targetSize, targetSize);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/jpeg", 0.85,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
